import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BookOpen, Globe, MoreVertical, Pencil, Trash2, Copy } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { EditMagazineDialog } from './EditMagazineDialog';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { useToast } from '@/hooks/useToast';

interface MagazineCardProps {
  event: NostrEvent;
  onDeleted?: () => void;
}

export function MagazineCard({ event, onDeleted }: MagazineCardProps) {
  const author = useAuthor(event.pubkey);
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const metadata = author.data?.metadata;

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isOwner = user?.pubkey === event.pubkey;

  const title = event.tags.find(([n]) => n === 'title')?.[1] ?? 'Untitled Magazine';
  const image = event.tags.find(([n]) => n === 'image')?.[1];
  const summary = event.tags.find(([n]) => n === 'summary')?.[1];
  const topics = event.tags.filter(([n]) => n === 't').map(([, v]) => v);
  const lang = event.tags.find(([n]) => n === 'l')?.[1];
  const dTag = event.tags.find(([n]) => n === 'd')?.[1] ?? '';

  const naddr = nip19.naddrEncode({ kind: event.kind, pubkey: event.pubkey, identifier: dTag });
  const npub = nip19.npubEncode(event.pubkey);

  const copyNaddr = async (e: React.MouseEvent) => {
    e.preventDefault();
    await navigator.clipboard.writeText(naddr).catch(() => {});
    toast({ title: 'Copied!', description: 'naddr copied to clipboard.' });
  };

  return (
    <>
      <div className="relative group">
        <Link to={`/${naddr}`}>
          <Card className="overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 h-full">
            <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
              {image ? (
                <img src={image} alt={title} className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-slate-400" />
                </div>
              )}
              {lang && (
                <Badge className="absolute top-2 right-2 bg-black/60 text-white border-0 text-xs">{lang.toUpperCase()}</Badge>
              )}
            </div>
            <CardContent className="p-3 space-y-1.5">
              <h3 className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">{title}</h3>
              {summary && <p className="text-xs text-muted-foreground line-clamp-2">{summary}</p>}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {metadata?.picture ? (
                  <img src={metadata.picture} alt="" className="w-4 h-4 rounded-full object-cover shrink-0" />
                ) : (
                  <Globe className="w-3 h-3 shrink-0" />
                )}
                <Link
                  to={`/${npub}`}
                  className="truncate hover:text-primary transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {metadata?.name ?? event.pubkey.slice(0, 8) + '…'}
                </Link>
              </div>
              {topics.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {topics.slice(0, 3).map(t => (
                    <Badge key={t} variant="secondary" className="text-xs px-1.5 py-0">{t}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Action menu — always visible on hover */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                onClick={(e) => e.preventDefault()}
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={copyNaddr}>
                <Copy className="w-4 h-4 mr-2" />Copy naddr
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEditOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" />Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <EditMagazineDialog open={editOpen} onOpenChange={setEditOpen} magazine={event} onSuccess={onDeleted} />
      <ConfirmDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} eventId={event.id} label="Magazine" onSuccess={onDeleted} />
    </>
  );
}
